import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CLASSES, DEVICES, ROTATE_TYPES, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { combineLatest, of, skip, Subscription, take } from 'rxjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { EngineService } from '@core/services/engine/engine.service';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { ModalService } from '@core/services/modal/modal.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SelectBackgroundModalComponent } from '@shared/modals/select-background-modal/select-background-modal.component';
import { QuizModalComponent } from '@shared/modals/quiz-modal/quiz-modal.component';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { BackgroundService } from '@core/services/background-service/background.service';
import { RotateDeviceModalComponent } from '@shared/modals/rotate-device-modal/rotate-device-modal.component';
import { AnotherDeviceModalComponent } from '@shared/modals/another-device-modal/another-device-modal.component';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { filter, first } from 'rxjs/operators';
import { Overlay } from '@angular/cdk/overlay';

import * as _ from 'lodash';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Component({
	selector: 'app-view-switcher',
	templateUrl: './view-switcher.component.html',
	styleUrls: ['./view-switcher.component.scss', './view-switcher.responsive.scss'],
})
export class ViewSwitcherComponent implements OnInit, OnDestroy {
	@Input() prodStatus: boolean;

	getModelLoaded: Subscription;
	getSessionBlindsData: Subscription;
	getViewHandler: Subscription;
	getSelectOpeningMode: Subscription;

	isDesignYourBlind = true;
	hasBlinds = false;
	quizStatuses = {
		dyb: false,
		iv: false,
		detection: false,
	};
	viewType: string;
	viewTypes = VIEW_TYPES;
	isSampleProject = null;
	isMobile: boolean;

	constructor(
		private shareService: ShareService,
		private engineService: EngineService,
		private sessionStorageService: SessionStorageService,
		private passDataService: PassDataService,
		private modalService: ModalService,
		private matDialog: MatDialog,
		private router: Router,
		private location: Location,
		private changeDetection: ChangeDetectorRef,
		private indexedDBService: IndexedDBService,
		private backgroundService: BackgroundService,
		public saveSessionBlind: SaveSessionBlindsService,
		private overlay: Overlay,
	) {}

	ngOnInit(): void {
		this.isMobile = this.passDataService.isMobileOrTablet;
		this.viewSwitcherHandler();
		this.showQuiz();
	}

	ngOnDestroy() {
		this.getModelLoaded.unsubscribe();
		this.getSessionBlindsData.unsubscribe();
		this.getViewHandler.unsubscribe();
	}

	viewSwitcherHandler() {
		this.loadingSwitcher(true);

		this.getModelLoaded = this.shareService.getSceneCreated.subscribe(() => {
			this.hasBlinds = !_.isEmpty(this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data));
			this.setCurrentBlindDesignSettings();
			this.modelViewerLoaderHandler(false);
			this.imageVisualisationModeHandler();
		});
		this.getSessionBlindsData = this.shareService.getSessionBlindsData.subscribe((res) => (this.hasBlinds = res));
		this.sessionStorageService.setBlindData(
			this.isDesignYourBlind ? VIEW_TYPES.design : VIEW_TYPES.image_visualisation,
			STORAGE_NAMES.zip_view_type,
		);

		this.getSelectOpeningMode = this.shareService.getSelectOpeningMode.subscribe(this.onSelectOpeningChange.bind(this));
		this.getViewHandler = this.shareService.getViewType.subscribe(this.onViewChange.bind(this));
		this.matDialog.afterOpened.subscribe((dialog) => {
			if (dialog.id === QuizModalComponent.ID) {
				this.isSampleProject = !!this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background)?.type;
			}
		});
	}

	imageVisualisationModeHandler(): void {
		// Called when navigating from Overview page or when reloading the page.
		const isImageVisualisation = this.sessionStorageService.getSession(STORAGE_NAMES.zip_image_visualisation_status);

		if (!isImageVisualisation) {
			this.loadingSwitcher(false);
			return;
		}

		const sessionId = this.location.path().split('?=')[1];
		const readyToSwitchObservable = sessionId
			? combineLatest([
					this.shareService.getSessionBlindsData, // Waiting for Blind Session Data
					this.shareService.getBlindSelect, // Waiting for selected Blind due to Interior Blind loading
				]).pipe(first())
			: of(null);

		readyToSwitchObservable.subscribe(() => setTimeout(this.onSwitchViewMode.bind(this, this.viewTypes.image_visualisation)));
	}

	onSwitchViewMode(type: string) {
		const toggleType = () => (this.isDesignYourBlind = type === this.viewTypes.design);
		const ivBackgroundImage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
		const sessionSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);
		const ivSceneSetting = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config).iv_scene_settings;
		const currentDevice = this.engineService.mobileAndTabletCheck() ? DEVICES.mobile : DEVICES.desktop;

		if (sessionSaved && ivBackgroundImage && ivSceneSetting && currentDevice !== ivSceneSetting.device) {
			const dialogRef = this.modalService.openResponsiveDialog({
				component: AnotherDeviceModalComponent,
				...this.modalService.getConfig(AnotherDeviceModalComponent.ID),
			});

			dialogRef.afterOpened().subscribe(() => this.loadingSwitcher(false));
			dialogRef.afterClosed().subscribe((continueStatus) => {
				if (continueStatus) {
					this.onSwitchViewMode(type);
				}
			});

			return;
		}

		if (type === this.viewTypes.design || !ivBackgroundImage) {
			toggleType();
		}

		if (type !== this.viewTypes.design) {
			if (!ivBackgroundImage) {
				this.loadingSwitcher(false);
				this.matDialog.open(SelectBackgroundModalComponent, this.modalService.getConfig(SelectBackgroundModalComponent.ID));
				this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
			} else {
				const isUploadImage = ivBackgroundImage.id.includes('own');
				const currentBackgroundImageId = isUploadImage
					? ivBackgroundImage.id
					: ivBackgroundImage.id.split('_').slice(0, -1).join('_') + `_${this.backgroundService.getScreenOrientation()}`;

				this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, currentBackgroundImageId).then((image) => {
					const switchViewMode = (currentImage) => {
						this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
						this.engineService.setBackgroundImage(currentImage);
						this.passDataService.isLiveOpened = false;

						this.shareService.getViewType.pipe(first()).subscribe(toggleType);
						setTimeout(this.loadingSwitcher.bind(this, false));
					};

					if (image) {
						image = {
							...image,
							theme: ivBackgroundImage.theme,
							settings: ivBackgroundImage.settings,
							shutterValues: ivBackgroundImage.shutterValues,
							sampleProjectBlinds: ivBackgroundImage.sampleProjectBlinds,
						};

						switchViewMode(image);
					} else {
						this.backgroundService.getSampleProjectImage(currentBackgroundImageId).then(switchViewMode);
					}
				});
			}
		} else {
			const sessionSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_session_saved);
			const changeViewType = () => {
				this.sessionStorageService.setBlindData(type, STORAGE_NAMES.zip_view_type);
				this.engineService.setBackgroundImage(null, true);
				this.modelViewerLoaderHandler(true);
				this.shareService.setViewType(this.viewTypes.design);
			};

			this.engineService.setModelAndBoundingBoxSettings();
			this.engineService.setIVSceneScreenShots().then(() => {
				this.engineService.saveModelAndBoundingBoxSettings(false);

				if (sessionSaved) {
					this.saveSessionBlind.PutStorageDataToServerPromise().then(() => changeViewType());
				} else {
					changeViewType();
				}
			});
		}
	}

	setCurrentBlindDesignSettings(): void {
		const id = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_current_blind_id);
		const type = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_type);

		if (id && type) {
			this.shareService.setBlindSelect({ id, type });
		}
	}

	showQuiz(): void {
		if (!this.hasBlinds) {
			return;
		}

		const selectOpeningMode = this.shareService.getSelectOpeningModeValue();
		const quizModal = this.matDialog.getDialogById(QuizModalComponent.ID);
		const openQuiz = () => {
			if (this.matDialog.getDialogById(QuizModalComponent.ID)) {
				return;
			}

			this.matDialog.open(QuizModalComponent, {
				...this.modalService.getConfig(QuizModalComponent.ID),
				scrollStrategy: this.overlay.scrollStrategies.noop(),
			});

			if (this.viewType) {
				this.quizStatuses.iv ||= !this.isDesignYourBlind && !selectOpeningMode;
				this.quizStatuses.dyb ||= this.isDesignYourBlind && !selectOpeningMode;
				this.quizStatuses.detection ||= selectOpeningMode;
			}

			this.sessionStorageService.setBlindData(this.quizStatuses, STORAGE_NAMES.zip_quiz_status);
		};

		const refreshQuiz = (status: boolean) => {
			if (status) {
				quizModal?.close();

				if (quizModal) {
					quizModal.afterClosed().subscribe(openQuiz);
				} else {
					openQuiz();
				}
			}
		};

		const isDesignType = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design;
		const storageStatuses = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_quiz_status);
		this.quizStatuses = storageStatuses || this.quizStatuses;
		const DYBQuizNotShowed = !this.quizStatuses.dyb && isDesignType;
		const IVQuizNotShowed = !this.quizStatuses.iv && !isDesignType;

		if (selectOpeningMode) {
			if (!this.quizStatuses.detection) {
				let detectionError = false;
				this.shareService.getDetectionError.pipe(take(1)).subscribe(() => (detectionError = true));
				this.shareService.getDetectionDone
					.pipe(
						skip(1),
						take(1),
						filter(() => !detectionError),
					)
					.subscribe((detectionDone) => refreshQuiz(detectionDone));
			}

			return;
		}

		refreshQuiz(DYBQuizNotShowed || IVQuizNotShowed);
		this.changeDetection.markForCheck();
	}

	onViewChange(type: string): void {
		this.sessionStorageService.setBlindData(type, STORAGE_NAMES.zip_view_type);

		if (type !== this.viewType) {
			this.showQuiz();
		}

		setTimeout(() => {
			this.isDesignYourBlind = type === VIEW_TYPES.design;
			this.passDataService.isLiveOpened = this.isDesignYourBlind ? false : this.passDataService.isLiveOpened;

			if (this.isDesignYourBlind) {
				this.engineService.closeVideoStream();
			} else {
				this.openRotateDeviceModal();
			}

			this.changeDetection.markForCheck();
		});

		this.viewType = type;
	}

	onSelectOpeningChange(): void {
		if (this.router.url.includes('/overview')) {
			return;
		}

		const quizModal = this.matDialog.getDialogById(QuizModalComponent.ID);

		if (quizModal) {
			quizModal.close();
			quizModal.afterClosed().subscribe(this.showQuiz.bind(this));
		} else {
			this.showQuiz();
		}
	}

	modelViewerLoaderHandler(status: boolean): void {
		document.querySelector(SELECTORS.model_viewer).classList.toggle('loader', status);
	}

	loadingSwitcher(status: boolean): void {
		document.querySelector(SELECTORS.wrapper).classList.toggle(CLASSES.loading, status);
	}

	openRotateDeviceModal(): void {
		const isOpen = this.matDialog.getDialogById(RotateDeviceModalComponent.ID);
		const isPhoneLandscape =
			this.backgroundService.getScreenOrientation() === 'landscape' && window.innerHeight <= 500 && window.innerWidth > window.innerHeight;
		const isSampleProject = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type) === 'project_sample';

		if (!isOpen && this.isMobile && isPhoneLandscape) {
			this.matDialog.open(
				RotateDeviceModalComponent,
				this.modalService.getConfig(RotateDeviceModalComponent.ID, { rotateType: ROTATE_TYPES.to_portrait, isSampleProject }),
			);
		}
	}
}
